// AiUsagePage — the operator's "AI usage and cost" pages (pen H-05..H-09):
// the admin frame (dark sidebar, header with the period, tabs) around the
// overview, the call list with its detail drawer, and the rate table.
//
// Pure projection: everything shown arrives as props (the route and the
// loaded reads); every change is `onNavigate(route)` (the URL is the view
// state) or `onCopy`. Local state is view-ephemeral only (hover, an
// unsubmitted search, "show all").

export type { AiUsageAccess, AiUsageRead };
export { AiUsagePage };

import type {
    AiUsageCallDetail,
    AiUsageCallList,
    AiUsageRates,
    AiUsageSummary,
} from "@bilda/app/agentCallAdmin";
import { appName } from "@bilda/app/identity";
import type { Remote } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { formatRange } from "../../app/aiUsage/format.js";
import type { PeriodRange } from "../../app/aiUsage/period.js";
import type { AiUsageRoute, AiUsageTab } from "../../app/aiUsage/route.js";
import type { ExternalError } from "../../app/externalError.js";
import { Icon } from "../Icon/Icon.js";
import { SegmentedControl } from "../SegmentedControl/SegmentedControl.js";
import { TextTabs } from "../TextTabs/TextTabs.js";
import { AiUsageCallDrawer } from "./AiUsageCallDrawer.js";
import { AiUsageCalls } from "./AiUsageCalls.js";
import { AiUsageOverview } from "./AiUsageOverview.js";
import { AiUsageRateTable } from "./AiUsageRateTable.js";
import { NoAccess } from "./AiUsageStates.js";
import "./AiUsage.css";

type AiUsageRead<T> = Remote.Remote<T, ExternalError>;

// Whether the viewer may see these pages (the server decides; a 401/403
// on a read turns this to "denied").
type AiUsageAccess = "allowed" | "denied";

type AiUsagePageProps = {
    readonly route: AiUsageRoute;
    readonly range: PeriodRange;
    readonly access: AiUsageAccess;
    // The signed-in operator, shown at the foot of the sidebar.
    readonly accountLabel: string | undefined;
    readonly summary: AiUsageRead<AiUsageSummary>;
    readonly calls: AiUsageRead<AiUsageCallList>;
    readonly detail: AiUsageRead<AiUsageCallDetail>;
    readonly rates: AiUsageRead<AiUsageRates>;
    readonly csvHref: string;
    readonly onNavigate: (route: AiUsageRoute) => void;
    readonly onRetry: () => void;
    readonly onCopy: (text: string) => void;
};

const AdminSidebar = ({
    accountLabel,
    access,
}: {
    readonly accountLabel: string | undefined;
    readonly access: AiUsageAccess;
}) => {
    const { t } = useLingui();
    return (
        <nav
            className="ai-usage__sidebar"
            data-theme="dark"
            aria-label={t`Administration`}
        >
            <div className="ai-usage__brand">
                <span className="ai-usage__brand-mark" aria-hidden="true">
                    <Icon name="shield" size="sm" />
                </span>
                <span className="ai-usage__brand-text">
                    <span className="ai-usage__brand-title">{t`Administration`}</span>
                    <span className="ai-usage__brand-sub">{appName}</span>
                </span>
            </div>
            {access === "denied" ? null : (
                <div className="ai-usage__nav">
                    <span className="ai-usage__nav-group">{t`People`}</span>
                    <a className="ai-usage__nav-item" href="/v1/admin/users">
                        <Icon name="person" size="sm" />
                        {t`Users`}
                    </a>
                    <span className="ai-usage__nav-group">{t`Usage`}</span>
                    <a
                        className="ai-usage__nav-item"
                        href="/admin/ai-usage"
                        aria-current="page"
                    >
                        <Icon name="sparkle" size="sm" />
                        {t`AI usage and cost`}
                    </a>
                </div>
            )}
            <div className="ai-usage__sidebar-foot">
                <a className="ai-usage__back" href="/">
                    <Icon name="chevron-left" size="sm" />
                    {t`Back to ${appName}`}
                </a>
                {accountLabel === undefined || access === "denied" ? null : (
                    <span className="ai-usage__account">
                        <span className="ai-usage__account-name">
                            {accountLabel}
                        </span>
                        <span className="ai-usage__account-role">
                            {t`Administrator`}
                        </span>
                    </span>
                )}
            </div>
        </nav>
    );
};

const AiUsagePage = (props: AiUsagePageProps) => {
    const { t, i18n } = useLingui();
    const { route, range, onNavigate } = props;
    const tabs: readonly { value: AiUsageTab; label: string }[] = [
        { value: "overview", label: t`Overview` },
        { value: "calls", label: t`Calls` },
        { value: "rates", label: t`Rate table` },
    ];
    const periods = [
        { value: "day", label: t`Day` },
        { value: "week", label: t`Week` },
        { value: "month", label: t`Month` },
    ];
    const setPeriod = (v: string) => {
        if (v === "day" || v === "week" || v === "month") {
            const { callId: _closed, ...rest } = route;
            onNavigate({ ...rest, period: v, page: 0 });
        }
    };
    return (
        <div className="ai-usage">
            <AdminSidebar
                accountLabel={props.accountLabel}
                access={props.access}
            />
            <main className="ai-usage__main">
                {props.access === "denied" ? (
                    <NoAccess />
                ) : (
                    <>
                        <header className="ai-usage__header">
                            <div className="ai-usage__heading">
                                <h1 className="ai-usage__title">{t`AI usage and cost`}</h1>
                                <p className="ai-usage__lede">
                                    {t`Every call the assistant makes, what it cost and how it went.`}
                                </p>
                            </div>
                            <div className="ai-usage__period">
                                <SegmentedControl
                                    options={periods}
                                    value={route.period}
                                    onChange={setPeriod}
                                    ariaLabel={t`Period`}
                                />
                                <span className="ai-usage__range">
                                    {formatRange(
                                        i18n.locale,
                                        range.from,
                                        range.last,
                                    )}
                                </span>
                            </div>
                        </header>
                        <TextTabs
                            tabs={tabs}
                            value={route.tab}
                            onChange={(tab) => {
                                const { callId: _closed, ...rest } = route;
                                onNavigate({ ...rest, tab, page: 0 });
                            }}
                            ariaLabel={t`AI usage views`}
                        />
                        <div className="ai-usage__content">
                            {route.tab === "overview" ? (
                                <AiUsageOverview
                                    route={route}
                                    summary={props.summary}
                                    onNavigate={onNavigate}
                                    onRetry={props.onRetry}
                                />
                            ) : route.tab === "calls" ? (
                                <AiUsageCalls
                                    route={route}
                                    calls={props.calls}
                                    csvHref={props.csvHref}
                                    onNavigate={onNavigate}
                                    onRetry={props.onRetry}
                                />
                            ) : (
                                <AiUsageRateTable
                                    route={route}
                                    rates={props.rates}
                                    onNavigate={onNavigate}
                                    onRetry={props.onRetry}
                                />
                            )}
                        </div>
                    </>
                )}
            </main>
            {route.tab === "calls" && route.callId !== undefined ? (
                <AiUsageCallDrawer
                    route={route}
                    detail={props.detail}
                    onNavigate={onNavigate}
                    onCopy={props.onCopy}
                    onRetry={props.onRetry}
                />
            ) : null}
        </div>
    );
};
